<!--
  EmailPreview.vue -- live sample-data preview of a transactional email
  (Phase 1 PR-4).

  A faux email client (traffic-lights + From/To/Subject meta) wrapping the
  rendered email card: header -> body -> CTA -> per-type detail block ->
  footer. Tokens resolve client-side to their SAMPLE value (from the
  catalog), except {store_name}, which resolves to the REAL store name so
  the preview matches what the backend sends. Updates on every keystroke
  via reactive props.

  Fallbacks (match the design + backend engine):
    - empty subject/body -> the type's DEFAULT (from the catalog).
    - brand toggle off  -> neutral accent (#455a64).
    - header image absent -> brand-colored header bar with the store name.
-->
<template>
  <div class="email-preview" data-testid="email-preview">
    <div class="email-preview__client">
      <span class="email-preview__dot email-preview__dot--r" />
      <span class="email-preview__dot email-preview__dot--y" />
      <span class="email-preview__dot email-preview__dot--g" />
      <span class="email-preview__client-label">Preview &middot; sample data</span>
      <span class="email-preview__inbox">Inbox</span>
    </div>

    <div class="email-preview__meta">
      <div><b>From</b>&nbsp; {{ senderName }} &lt;no-reply@threadflow.store&gt;</div>
      <div>
        <b>To</b>&nbsp;
        <span data-testid="email-preview-recipient">{{ recipientLine }}</span>
        <span
          v-if="typeObj && typeObj.audience"
          class="email-preview__audience"
          data-testid="email-preview-audience"
          >&middot; {{ typeObj.audience }}</span
        >
      </div>
      <div>
        <b>Subject</b>&nbsp;
        <span data-testid="email-preview-subject">{{ resolvedSubject }}</span>
      </div>
    </div>

    <div class="email-preview__canvas">
      <div class="email-preview__card">
        <!-- Header: image banner OR brand-colored bar with store name -->
        <div
          v-if="headerImage"
          class="email-preview__banner"
          data-testid="email-preview-header-image"
        >
          <img
            class="email-preview__banner-img"
            :src="headerImage"
            alt="Email header"
          />
        </div>
        <div
          v-else
          class="email-preview__header-bar"
          :style="{ background: accent }"
          data-testid="email-preview-header-bar"
        >
          <span class="email-preview__store-name">{{ storeSample }}</span>
        </div>

        <!-- Body (resolved, sample data). Distributor-authored HTML that the
             backend re-sanitizes on write before any shopper sees it; here
             it is the store owner's own draft rendered in their own browser
             with all tokens resolved to safe sample values. -->
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div
          class="email-preview__body"
          data-testid="email-preview-body"
          v-html="resolvedBody"
        />

        <div class="email-preview__cta-wrap">
          <a
            class="email-preview__cta"
            :style="{ background: accent }"
            href="#"
            @click.prevent
            data-testid="email-preview-cta"
            >{{ typeObj ? typeObj.cta : "" }}</a
          >
        </div>

        <!-- Per-type structured detail block -->
        <div
          class="email-preview__detail"
          :data-testid="`email-preview-detail-${detailVariant}`"
        >
          <template v-if="detailVariant === 'order'">
            <div class="email-preview__row">
              <span><span class="email-preview__q">1x</span>Embroidered Polo -- Navy, L</span>
              <span>$32.00</span>
            </div>
            <div class="email-preview__row">
              <span><span class="email-preview__q">1x</span>Structured Cap -- Black</span>
              <span>$15.50</span>
            </div>
            <div class="email-preview__tot">
              <span>Order #1042 &middot; Total</span><span>$47.50</span>
            </div>
          </template>
          <template v-else-if="detailVariant === 'tracking'">
            <div class="email-preview__row">
              <span class="email-preview__k">Order</span><span>#1042</span>
            </div>
            <div class="email-preview__row">
              <span class="email-preview__k">Carrier</span><span>UPS</span>
            </div>
            <div class="email-preview__tot">
              <span>Tracking number</span
              ><span class="email-preview__mono">1Z999AA10123456784</span>
            </div>
          </template>
          <template v-else-if="detailVariant === 'refund'">
            <div class="email-preview__row">
              <span class="email-preview__k">Order</span><span>#1042</span>
            </div>
            <div class="email-preview__row">
              <span class="email-preview__k">Reason</span
              ><span>Item out of stock</span>
            </div>
            <div class="email-preview__tot">
              <span>Refunded to original payment</span
              ><span :style="{ color: accent }">$32.00</span>
            </div>
          </template>
          <template v-else>
            <div
              class="email-preview__callout"
              :style="{
                borderColor: accent + '66',
                background: accent + '14',
              }"
            >
              <v-icon :style="{ color: accent }" aria-hidden="true"
                >schedule</v-icon
              >
              <div>
                <div class="email-preview__callout-title">
                  Closes March 28, 2026 &middot; 18 orders to review
                </div>
                <div class="email-preview__callout-sub">
                  Shoppers can't order after this date. Review before it
                  closes.
                </div>
              </div>
            </div>
          </template>
        </div>

        <div class="email-preview__foot">
          {{ storeSample }} &middot;
          <span :style="{ color: accent }">{{ storeUrlSample }}</span
          ><br />
          Powered by ThreadFlow &middot;
          {{
            typeObj && typeObj.footer_note
              ? typeObj.footer_note
              : "You received this because you shopped this store."
          }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  resolveBodyHtml,
  resolveSubjectText,
} from "./tokenHtml";

const NEUTRAL_ACCENT = "#455a64";
const BRAND_ACCENT = "#119ae9";

export default {
  name: "EmailPreview",

  props: {
    // Catalog entry for the active type (label, cta, detail, default_*, ...).
    typeObj: { type: Object, default: null },
    // Token-keyed subject/body HTML (chip form) from the editors.
    subjectHtml: { type: String, default: "" },
    bodyHtml: { type: String, default: "" },
    // Sender from-name (store-level).
    fromName: { type: String, default: "" },
    // Whether to use the store brand accent (else neutral).
    useBrand: { type: Boolean, default: true },
    // Resolved header image URL (or null for the brand-bar fallback).
    headerImage: { type: String, default: null },
    // Real store name. The backend resolves {store_name} from the store at
    // send time, so the preview shows the real name rather than the catalog's
    // generic sample -- otherwise every store previews as "Samsung Spring 2026".
    storeName: { type: String, default: "" },
    // Real storefront URL (base + /shop/<slug>), built by the parent to match
    // what the backend's frontend_url() emits. Empty when the store has no
    // slug yet, which falls back to the catalog sample.
    storeUrl: { type: String, default: "" },
  },

  computed: {
    // key -> sample map from the active type's catalog tokens. store_name is
    // overridden with the real store name (when we have one) so the header,
    // footer, subject and body all show what the shopper will actually get.
    sampleMap() {
      const map = {};
      const toks = (this.typeObj && this.typeObj.tokens) || [];
      toks.forEach((t) => {
        map[t.key] = t.sample;
      });
      const real = this.storeName && this.storeName.trim();
      if (real) {
        map.store_name = real;
      }
      const realUrl = this.storeUrl && this.storeUrl.trim();
      if (realUrl) {
        map.store_url = realUrl;
      }
      return map;
    },
    // key -> pre-rendered MARKUP, for the one token that renders as markup.
    //
    // Served by the backend catalog as ``sample_html`` (today only
    // ``order_link``, which the send path resolves to an anchor whose text
    // is the order number). Read from the catalog rather than built here so
    // there is ONE definition of what this token renders as -- the preview
    // shows the link the customer will receive instead of visible
    // "<a href=...>" characters, and it cannot drift from the send path.
    //
    // Every other token resolves as escaped TEXT (see tokenHtml.js).
    htmlMap() {
      const map = {};
      const toks = (this.typeObj && this.typeObj.tokens) || [];
      toks.forEach((t) => {
        if (t.sample_html) {
          map[t.key] = t.sample_html;
        }
      });
      return map;
    },
    accent() {
      return this.useBrand ? BRAND_ACCENT : NEUTRAL_ACCENT;
    },
    senderName() {
      return (this.fromName && this.fromName.trim()) || "ThreadFlow";
    },
    storeSample() {
      return this.sampleMap.store_name || "Your store";
    },
    storeUrlSample() {
      // Fallback only -- the real store URL is injected above when the
      // campaign has one. It was "yourstore.threadflow.store", a per-store
      // subdomain nobody serves and not even a URL (no scheme), so the
      // preview taught a shape that would have been a dead link. Mirrors
      // the store_url sample in the backend token catalog.
      return this.sampleMap.store_url || "https://shop.threadflow.com/yourstore";
    },
    recipientLine() {
      // store_closing goes to a manager; others to a shopper.
      if (this.typeObj && this.typeObj.detail === "closing") {
        return "Jordan Lee <jordan.lee@acme.com>";
      }
      return "Sarah Chen <sarah.chen@samsung.com>";
    },
    detailVariant() {
      return (this.typeObj && this.typeObj.detail) || "order";
    },
    resolvedBody() {
      const src =
        this.bodyHtml && this.bodyHtml.trim()
          ? this.bodyHtml
          : (this.typeObj && this.typeObj.default_body) || "";
      return resolveBodyHtml(
        src,
        (k) => this.sampleMap[k],
        (k) => this.htmlMap[k]
      );
    },
    resolvedSubject() {
      const src =
        this.subjectHtml && this.subjectHtml.trim()
          ? this.subjectHtml
          : (this.typeObj && this.typeObj.default_subject) || "";
      return resolveSubjectText(src, (k) => this.sampleMap[k]);
    },
  },
};
</script>

<style scoped>
.email-preview {
  border: 1px solid var(--tf-border, #cfd8e3);
  border-radius: 14px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 6px 20px rgba(19, 55, 82, 0.08);
}
.email-preview__client {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  background: #f4f6f9;
  border-bottom: 1px solid var(--tf-border, #cfd8e3);
}
.email-preview__dot {
  width: 11px;
  height: 11px;
  border-radius: 50%;
}
.email-preview__dot--r {
  background: #ff5f57;
}
.email-preview__dot--y {
  background: #febc2e;
}
.email-preview__dot--g {
  background: #28c840;
}
.email-preview__client-label {
  margin-left: 8px;
  font-size: 11.5px;
  color: var(--tf-fg-4, #78909c);
  letter-spacing: 0.4px;
}
.email-preview__inbox {
  margin-left: auto;
  font-size: 10.5px;
  color: var(--tf-fg-5, #90a4ae);
  text-transform: uppercase;
  letter-spacing: 1px;
}
.email-preview__meta {
  padding: 12px 16px;
  border-bottom: 1px solid var(--tf-border, #cfd8e3);
  font-size: 12.5px;
  color: var(--tf-fg-2, #37474f);
  line-height: 1.9;
}
.email-preview__audience {
  margin-left: 8px;
  font-size: 10.5px;
  color: var(--tf-fg-5, #90a4ae);
  text-transform: uppercase;
  letter-spacing: 0.6px;
}
.email-preview__canvas {
  padding: 16px;
  background: #eef1f5;
}
.email-preview__card {
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e7ebf0;
}
.email-preview__banner-img {
  display: block;
  width: 100%;
  max-height: 120px;
  object-fit: cover;
}
.email-preview__header-bar {
  height: 64px;
  display: flex;
  align-items: center;
  padding: 0 30px;
}
.email-preview__store-name {
  color: #fff;
  font-family: "Poppins", "Roboto", sans-serif;
  font-weight: 600;
  font-size: 18px;
}
.email-preview__body {
  padding: 22px 30px 6px;
  font-size: 14px;
  color: var(--tf-fg-2, #37474f);
  line-height: 1.6;
}
.email-preview__body :deep(p) {
  margin: 0 0 12px;
}
.email-preview__cta-wrap {
  padding: 0 30px;
}
.email-preview__cta {
  display: inline-block;
  padding: 10px 20px;
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
}
.email-preview__detail {
  margin: 18px 30px 8px;
}
.email-preview__row,
.email-preview__tot {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  font-size: 13px;
  color: var(--tf-fg-2, #37474f);
  border-bottom: 1px solid #eef1f5;
}
.email-preview__tot {
  font-weight: 600;
  border-bottom: none;
  border-top: 1px solid #e0e5ea;
  margin-top: 4px;
}
.email-preview__q {
  display: inline-block;
  color: var(--tf-fg-4, #78909c);
  margin-right: 8px;
}
.email-preview__k {
  color: var(--tf-fg-4, #78909c);
}
.email-preview__mono {
  font-family: ui-monospace, Menlo, monospace;
  font-size: 12px;
}
.email-preview__callout {
  display: flex;
  gap: 12px;
  align-items: center;
  border: 1px solid;
  border-radius: 8px;
  padding: 14px 16px;
}
.email-preview__callout-title {
  font-weight: 600;
  font-size: 14px;
  color: var(--tf-fg-1, #133752);
}
.email-preview__callout-sub {
  font-size: 12.5px;
  color: var(--tf-fg-3, #546e7a);
  margin-top: 2px;
}
.email-preview__foot {
  padding: 18px 30px 22px;
  font-size: 11.5px;
  color: var(--tf-fg-4, #78909c);
  line-height: 1.7;
  border-top: 1px solid #eef1f5;
  margin-top: 12px;
}
</style>
